import { useAppSelector } from '@app/core/hooks/redux-hooks';
import { FC, useMemo } from 'react';
import CellValue from '@app/shared/components/cell-value/CellValue';
import {
  amountRules,
  decimalCalculatedViewRules,
  enteredPercentageViewRules,
} from '@app/shared/components/cell-value/CellValueConfigurations';
import { TransactionType } from '@app/shared/models/contracts/enums/shared-enums';
import { useLocale } from '@app/core/hooks/useLocale';
import {
  getWaterfallCaseYearValue,
  getYearTransactionsGroupedByCaseAndYear,
} from '@app/core/store/pwerm2-calculation-slice-selectors';
import { getCalculatedProjectValueInPercent } from '@app/shared/formatters';
import { Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto } from '@app/shared/models/contracts/pwerm2-calculation-results-dto';
import { InterimsByTypeSection } from './InterimsByTypeSection';
import TableSectionValuesPerForecast from '../../components/TableSectionValuesPerForecast';

interface InterimCashFlowsSectionProps {
  columnsCount: number;
}

const mergeTransactionRecords = (
  ...records: Array<
    Record<
      string,
      Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
    >
  >
): Record<
  string,
  Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
> => {
  const merged: Record<
    string,
    Record<string, Pwerm2CalculationWaterfallCaseYearInstrumentMovementTransactionDto[]>
  > = {};

  for (const record of records) {
    for (const caseId of Object.keys(record)) {
      if (!merged[caseId]) {
        merged[caseId] = {};
      }
      for (const year of Object.keys(record[caseId])) {
        if (!merged[caseId][year]) {
          merged[caseId][year] = [];
        }
        merged[caseId][year] = merged[caseId][year].concat(record[caseId][year]);
      }
    }
  }

  return merged;
};

const InterimCashFlowsSection: FC<InterimCashFlowsSectionProps> = ({ columnsCount }) => {
  const results = useAppSelector((state) => state.pwerm2Calculation.calculatedResults);
  const { l } = useLocale();

  const infusionTransactions = useMemo(
    () => getYearTransactionsGroupedByCaseAndYear(results, (t) => t.infusionTransactions),
    [results]
  );

  const redemptionTransactions = useMemo(
    () => getYearTransactionsGroupedByCaseAndYear(results, (t) => t.redemptionTransactions),
    [results]
  );

  const cashPaidTransactions = useMemo(
    () => getYearTransactionsGroupedByCaseAndYear(results, (t) => t.cashPaidTransactions),
    [results]
  );

  const dividendTransactions = useMemo(
    () => getYearTransactionsGroupedByCaseAndYear(results, (t) => t.dividendTransactions),
    [results]
  );

  const movementTransactionsByCaseAndYear = useMemo(
    () => mergeTransactionRecords(infusionTransactions, redemptionTransactions),
    [infusionTransactions, redemptionTransactions]
  );

  const haveCashPaidTransactions = useMemo(
    () =>
      Object.values(cashPaidTransactions).some((yearMap) =>
        Object.values(yearMap).some((transactions) => transactions.length > 0)
      ),
    [cashPaidTransactions]
  );

  const haveDividendTransactions = useMemo(
    () =>
      Object.values(dividendTransactions).some((yearMap) =>
        Object.values(yearMap).some((transactions) => transactions.length > 0)
      ),
    [dividendTransactions]
  );

  const movementEventNarratives = useMemo(() => {
    const transactions = Object.values(movementTransactionsByCaseAndYear)
      .flatMap((transactionsByYear) => Object.values(transactionsByYear).flat())
      .sort((a, b) => a.date.localeCompare(b.date));

    return Array.from(new Set(transactions.map((t) => t.eventNarrative).filter(Boolean)));
  }, [movementTransactionsByCaseAndYear]);

  const cashOrDividendDiscountFactorTitle =
    haveCashPaidTransactions && haveDividendTransactions
      ? l('_CashInterestAndDividends')
      : haveCashPaidTransactions
      ? l('_CashInterest')
      : haveDividendTransactions
      ? l('_Dividends')
      : null;

  return (
    <>
      <tr data-testid="interim-cashflows-header-row">
        <th className="table-primary__cell--header">{l('_InterimCashFlows')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header" />
      </tr>
      <InterimsByTypeSection
        transactionsByCaseAndYear={infusionTransactions}
        transactionType={TransactionType.Infusion}
        headerLabel={l('_Infusion')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
        colCount={columnsCount}
      />
      <InterimsByTypeSection
        transactionsByCaseAndYear={redemptionTransactions}
        transactionType={TransactionType.Redemption}
        headerLabel={l('_Redemption')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
        colCount={columnsCount}
      />
      <InterimsByTypeSection
        transactionsByCaseAndYear={cashPaidTransactions}
        transactionType={TransactionType.CashPaid}
        headerLabel={l('_CashInterest')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
        colCount={columnsCount}
      />
      <InterimsByTypeSection
        transactionsByCaseAndYear={dividendTransactions}
        transactionType={TransactionType.Dividend}
        headerLabel={l('_Dividends')}
        valueSelector={(transaction) => transaction?.amount}
        cellValueFormattingRules={amountRules}
        colCount={columnsCount}
      />
      <tr data-testid="cost-of-total-equity-header-row">
        <th className="table-primary__cell--header-tertiary">{l('_CostOfTotalEquity')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>
      <TableSectionValuesPerForecast
        title={l('_CostOfTotalEquity')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...enteredPercentageViewRules}
            value={getCalculatedProjectValueInPercent(
              Number(
                getWaterfallCaseYearValue(
                  results,
                  caseItem.caseId,
                  forecast.forecastId,
                  (year) => year.cumulativeCostOfEquityPc
                )
              )
            )}
            dataTestId={`cost-of-total-equity-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_DiscountFactor')}
        renderSpacer={false}
        renderCell={() => null}
        additionalTitleClassNames="table-primary__cell--strong"
      />
      {movementEventNarratives.map((eventNarrative, index) => (
        <TableSectionValuesPerForecast
          key={`discountFactor-ir-${eventNarrative}`}
          title={eventNarrative}
          renderSpacer={false}
          renderCell={({ forecast, caseItem }) => {
            const eventTransaction = movementTransactionsByCaseAndYear[caseItem.caseId]?.[
              forecast.forecastYear
            ]?.find((t) => t.eventNarrative === eventNarrative);

            return (
              <CellValue
                {...decimalCalculatedViewRules}
                value={eventTransaction?.discountFactor ?? null}
                dataTestId={`discountFactor-ir-cell-${caseItem.caseId}-${forecast.forecastId}-${index}`}
              />
            );
          }}
        />
      ))}
      {cashOrDividendDiscountFactorTitle && (
        <TableSectionValuesPerForecast
          title={cashOrDividendDiscountFactorTitle}
          renderSpacer
          renderCell={({ forecast, caseItem }) => {
            const haveCashPaidTransactionsOnYear =
              cashPaidTransactions[caseItem.caseId]?.[forecast.forecastYear]?.length > 0;

            const haveDividendTransactionsOnYear =
              dividendTransactions[caseItem.caseId]?.[forecast.forecastYear]?.length > 0;

            const value =
              haveCashPaidTransactionsOnYear || haveDividendTransactionsOnYear
                ? getWaterfallCaseYearValue(
                    results,
                    caseItem.caseId,
                    forecast.forecastId,
                    (x) => x.cumulativeDiscountFactor
                  )
                : null;

            return (
              <CellValue
                {...decimalCalculatedViewRules}
                value={value}
                dataTestId={`discountFactor-cash-dividends-cell-${caseItem.caseId}-${forecast.forecastId}`}
              />
            );
          }}
        />
      )}
      <tr data-testid="pv-header-row">
        <th className="table-primary__cell--header-tertiary">{l('_PV')}</th>
        <th colSpan={columnsCount - 1} className="table-primary__cell--header-tertiary" />
      </tr>
      <TableSectionValuesPerForecast
        title={l('_PVOfNetInterimCashFlows')}
        renderSpacer={false}
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.pvOfInterimCashFlows
            )}
            dataTestId={`present-value-interim-cash-flows-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
      <TableSectionValuesPerForecast
        title={l('_CumulativePVOfNetInterimCashFlows')}
        renderSpacer
        renderCell={({ caseItem, forecast }) => (
          <CellValue
            {...amountRules}
            value={getWaterfallCaseYearValue(
              results,
              caseItem.caseId,
              forecast.forecastId,
              (year) => year.cumulativePvOfInterimCashFlows
            )}
            dataTestId={`present-value-interim-cash-flows-cumulative-cell-${caseItem.caseId}-${forecast.forecastId}`}
          />
        )}
      />
    </>
  );
};

export default InterimCashFlowsSection;
